import _axios from 'axios'
import flatten, { unflatten } from 'flat'
import { print } from 'graphql/language/printer'
import { captureException } from '@sentry/browser'
import { ASTNode } from 'graphql/language/ast'
import env from '../env'
import { useAuthTokenInterceptor } from './jwt'
import { requestRefresh } from './auth'
import { Files } from '../graphql/types'

/**
 * Example:

     import gql from 'graphql-tag'

     interface CurrentUserQuery {
        currentUser: {
            id: number
            firstName: string
            lastName: string
            mugshot: string
        }
    }

     const getCurrentUser = graphql.createQuery<CurrentUserQuery>(gql`
            {
                currentUser {
                    id
                    firstName
                    lastName
                    mugshot
                }
            }
     `)

     ...

     const currentUser = await getCurrentUser()
 */

export type QueryResponse<T> = {
    errors: string[]
    data: T
}

export interface Pagination {
    after?: number
    before?: number
    first?: number
    last?: number
    filters?: string[]
}

export interface PaginationWithEvent extends Pagination {
    eventId?: string | number
}

export const axios = _axios.create({
    baseURL: env.graphqlUrl,
    headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
    },
})

// eslint-disable-next-line react-hooks/rules-of-hooks
useAuthTokenInterceptor(axios, { requestRefresh })

export function createResponse<T>(response): QueryResponse<T> {
    const responseData = response?.data
    const data = responseData?.data
    const errors = response.errors || responseData?.errors || []

    if (!data) {
        return {
            data: undefined,
            errors: errors.length ? errors.map((e) => e.message) : ['Something went wrong'],
        }
    }

    return { errors, data }
}

// convert some error string to graphql-structure error response
export function createErrorResponse(errorMessage: string): QueryResponse<undefined> {
    return {
        data: undefined,
        errors: [errorMessage],
    }
}

const isFile = (v: any): v is File => v instanceof File
const mapFilesToVariables = (variables: Record<string, any>): [any, Files] => {
    const flatCopy = flatten(variables)
    const files = {}
    Object.entries(flatten(flatCopy)).forEach(([key, value]) => {
        if (!isFile(value)) { return }
        files[key] = {
            file: value,
            name: value.name,
        }
        flatCopy[key] = null
    })
    return [unflatten(flatCopy), files]
}

const createFormData = (operation: string, files: Files) => {
    const formData = new FormData()
    const fileMap = {}

    Object.entries(files).forEach(([path, { file, name }], index) => {
        fileMap[`${index}`] = [`variables.${path}`]
        formData.append(`${index}`, file, name)
    })

    formData.append('map', JSON.stringify(fileMap))
    formData.append('operations', operation)
    return formData
}

const query = <Data = any, Variables = undefined>(
    queryNode: ASTNode,
    variables: Variables,
): Promise<QueryResponse<Data>> => {
    const [mappedVariables, files] = mapFilesToVariables(variables)
    const queryString = `${print(queryNode)}`
    const operation = JSON.stringify({ query: queryString, variables: mappedVariables })
    const data = Object.values(files || {}).length > 0
        ? createFormData(operation, files)
        : operation

    return new Promise((resolve, reject) => {
        axios.post('', data)
            .then((response) => resolve(createResponse<Data>(response)))
            .catch((error) => {
                // Failed fetch is always an unexpected error, since expected
                // errors will be in "response.errors". Send it to sentry.
                captureException(error)
                reject(createErrorResponse(error))
                return error
            })
    })
}

const createQuery = <Data = any, Variables = any>(queryNode: ASTNode) => (
    (variables?: Variables) => query<Data, Variables>(queryNode, variables)
)

export default { query, createQuery }
